import {
  DEFAULT_PAYMENT_TERMS, HONORIFIC_LABEL, PARTNER_MSG, QUALIFIED_STATUS_LABEL, TERM_DAY_LABEL, TERM_MONTH_LABEL, TRANSFER_FEE_LABEL, TRANSFER_FEE_NOTE, formatZip, paymentTermsLabel, stripCorporateWords,
  type CorporateInfo, type Honorific, type Partner, type PartnerCreate, type PartnerKind, type PartnerType, type PaymentTerms, type TermDay, type TermMonth, type TransferFeeBearer,
  PARTNER_PICKER_MSG,
  partnerCodeOf,
} from "@genbaryoku/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { AddressFields } from "../components/AddressFields.jsx";
import { CorporateLookupButton, CorporateLookupDialog, NtaSource, type CorporatePatch } from "../components/CorporateLookup.jsx";
import { PhoneFields } from "../components/PhoneFields.jsx";
import { ErrorText, Field, Help, guardComposingEnter, useToast } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { CONTACTS_HELP, DAYS, HONORIFICS, MONTHS, SHORT_NAME_HELP } from "./Partner.jsx";

type Form = {
  name: string; partnerType: PartnerType; shortName: string; nameKana: string; kind: PartnerKind; honorific: Honorific;
  phone: string; fax: string; postalCode: string; address: string; building: string;
  corporateNumber: string; invoiceRegistrationNo: string; transferFeeBearer: TransferFeeBearer; memo: string;
  terms: PaymentTerms;
  contactName: string; contactDepartment: string; contactTitle: string; mobile: string; email: string;
  /** #288：顧客コード・仕入先コード（任意） */
  customerCode: string; vendorCode: string;
};
const EMPTY: Form = {
  name: "", partnerType: "client", shortName: "", nameKana: "", kind: "corporate", honorific: "御中",
  phone: "", fax: "", postalCode: "", address: "", building: "",
  corporateNumber: "", invoiceRegistrationNo: "", transferFeeBearer: "customer", memo: "",
  terms: DEFAULT_PAYMENT_TERMS,
  contactName: "", contactDepartment: "", contactTitle: "", mobile: "", email: "",
  customerCode: "", vendorCode: "",
};
const sameTerms = (a: PaymentTerms, b: PaymentTerms) => a.closingDay === b.closingDay && a.paymentMonthOffset === b.paymentMonthOffset && a.paymentDay === b.paymentDay;
/** 登録の画面の欄の id（間違いの欄へカーソルを送る。詳細の画面の pd_ と同じ考え方） */
const FID = {
  name: "pn_name", shortName: "pn_shortName", nameKana: "pn_nameKana", phone: "pn_phone", fax: "pn_fax", postalCode: "pn_postalCode", address: "pn_address", building: "pn_building",
  corporateNumber: "pn_corporateNumber", invoiceRegistrationNo: "pn_invoiceRegistrationNo", memo: "pn_memo", contactName: "pn_contactName", mobile: "pn_mobile", email: "pn_email",
  customerCode: "pn_customerCode", vendorCode: "pn_vendorCode",
} as const satisfies Record<string, string>;

/**
 * 顧客の登録（/partners/new）。2026-09-23 射場さん：一覧から「{customer}を登録」で別の画面に → 同じ日に「詳細の画面と同じように入力できるように」。
 * 詳細（/partners/:id）と同じ並び：左に 名前と宛名／住所と連絡先／請求／法人番号と登録番号／社内メモ、右に 帳票の宛名／担当者（1人目）／支店・営業所（登録のあとで）。
 * 必須は名前と略称だけ（ルール6.3）。略称は名前から「株式会社」などを外して自動で入る（直したらそのまま）。1回の POST /partners で全部を登録し、詳細へ
 * 9/29（#92、docs/35）：欄の下の説明は「？」へ（見出しの「？」・「請求」の「？」）。「法人番号・登録番号は入力できます。」は無くした（決め10）。
 *   支店・営業所の段は見出しを「（登録のあとで追加）」にして「？」を外した（上に並ぶ「？」を3つに）
 */
import { BackButton, differs, useBackNav, usePageName } from "../components/BackButton.jsx";

export function PartnerNewPage() {
  const L = useLabels();
  const me = useMe();
  const canLookup = !!me.data?.features.corporateLookup;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node } = useToast();
  /** 顧客の一覧の「外注先」から来たら外注先として登録（047。?type=vendor） */
  const [sp] = useSearchParams();
  const fromVendor = sp.get("type") === "vendor";
  const [form, setForm] = useState<Form>(() => ({ ...EMPTY, partnerType: fromVendor ? "vendor" : "client" }));
  /** 見せる言葉：顧客／外注先（両方なら「顧客・外注先」） */
  const who = form.partnerType === "vendor" ? L.vendor : form.partnerType === "both" ? `${L.customer}・${L.vendor}` : L.customer;
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ{customer}（外注先から来たら外注先）の一覧。入れかけは確かめる
  const first = useRef(form);
  const back = useBackNav(fromVendor ? { to: "/partners?type=vendor", label: L.vendor } : { to: "/partners", label: L.customer }, differs(form, first.current));
  usePageName(`${who}登録`);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [lookup, setLookup] = useState(false);
  /** 略称を自分で直したら、名前を変えても上書きしない */
  const shortTouched = useRef(false);
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));
  const setName = (name: string) => set(shortTouched.current ? { name } : { name, shortName: stripCorporateWords(name) });

  const create = useMutation({
    mutationFn: (v: PartnerCreate) => api<Partner>("POST", "/partners", v),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["partners"] });
      // 登録のときに番号チェックが流れたら結果も一緒に。国税庁に届かなかったら詳細の画面で「再試行」（docs/23 §4）
      const head = `${p.name} ${p.honorific} を登録しました。`;
      const msg = p.autoCheck === "checked" && p.qualified ? `${head}番号チェック：${QUALIFIED_STATUS_LABEL[p.qualified.status]}` : head;
      nav(`/partners/${p.id}`, { state: { toast: msg, checkFailed: p.autoCheck === "failed" } });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        const errs: Record<string, string> = {};
        for (const [k, v] of Object.entries(e.problem.errors)) errs[k === "paymentTerms" ? "terms" : k] = t(v);
        setErrors(errs);
        const first = Object.keys(errs)[0];
        const fid = first ? (FID as Record<string, string | undefined>)[first] : undefined;
        if (fid) document.getElementById(fid)?.focus();
        return;
      }
      setServerError(messageOf(e));
    },
  });
  const submit = () => {
    setServerError(undefined);
    if (!form.name.trim()) {
      setErrors({ name: t(PARTNER_MSG.nameRequired) });
      document.getElementById(FID.name!)?.focus();
      return;
    }
    if (!form.shortName.trim()) {
      setErrors({ shortName: PARTNER_MSG.shortNameRequired });
      document.getElementById(FID.shortName!)?.focus();
      return;
    }
    setErrors({});
    const { terms, customerCode, vendorCode, ...rest } = form;
    // 既定の支払条件のままなら送らない（顧客に支払条件の行を作らない＝詳細の画面の「（既定）」と同じ）。
    // コードは役割があるものだけ（#288）
    create.mutate({
      ...rest,
      ...(sameTerms(terms, DEFAULT_PAYMENT_TERMS) ? {} : { paymentTerms: terms }),
      ...(form.partnerType !== "vendor" && customerCode.trim() ? { customerCode } : {}),
      ...(form.partnerType !== "client" && vendorCode.trim() ? { vendorCode } : {}),
    });
  };
  /** 会社情報取得の「反映」：チェックした項目だけ（会社名を入れたときは略称（直していなければ）・法人・敬称も合わせる） */
  const applyCorporate = (patch: CorporatePatch, c: CorporateInfo) => {
    set({
      ...patch,
      ...(patch.name !== undefined
        ? { ...(shortTouched.current ? {} : { shortName: stripCorporateWords(patch.name) }), kind: "corporate" as const, honorific: form.kind === "corporate" ? form.honorific : "御中" }
        : {}),
    });
    setLookup(false);
    toast(`国税庁の登録「${c.name}」を入れました。確かめて「${who}登録」を押してください。`);
  };
  const firstError = Object.values(errors)[0];
  const H2 = ({ children }: { children: React.ReactNode }) => <h2 style={{ margin: "0 0 10px", fontSize: "var(--fs-19)" }}>{children}</h2>;
  const sec = { padding: "16px 22px 18px", marginBottom: 14 } as const;
  const sel = (id: string, label: string, value: number, opts: number[], fmt: (n: number) => string, onPick: (n: number) => void) => (
    <label className="row" style={{ gap: 6, alignItems: "center" }}>
      {label ? <span style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{label}</span> : null}
      <select id={id} className="inp termsel" value={value} onChange={(e) => onPick(Number(e.target.value))}>
        {opts.map((o) => (
          <option key={o} value={o}>{fmt(o)}</option>
        ))}
      </select>
    </label>
  );

  return (
    <>
      <BackButton back={back} />
      {back.dialog}
      <div className="hrow" style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0 }} data-testid="partner-new-title">
          {who}登録
          <Help testId="help-register" label="登録の説明" text={`必須は名前と略称だけです。電話・FAX・住所・担当者・社内メモは暗号化して保存します。2人目からの担当者と支店・営業所は、登録のあとの画面で追加します。${canLookup ? "法人番号か登録番号があれば、登録のときに国税庁の公表サイトで番号チェックをします。" : ""}`} />
        </h1>
        <span className="sp" />
      </div>

      <div className="pdgrid">
        <form
          className="pdmain"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {/* ---- 名前と宛名 ---- */}
          <section className="paper" style={sec}>
            <H2>名前と宛名</H2>
            <div className="row" style={{ alignItems: "flex-end" }}>
              {/* 会社情報取得（国税庁）は名前の欄の中の右端（2026-10-14 案A。詳細の画面と同じ。docs/23 §4） */}
              <Field id={FID.name} label="名前（会社名、または個人のお名前）" style={{ flex: "2 1 300px" }} end={canLookup ? <CorporateLookupButton onClick={() => setLookup(true)} /> : undefined}>
                <input id={FID.name} className="inp" autoComplete="organization" autoFocus value={form.name} onKeyDown={guardComposingEnter} onChange={(e) => setName(e.target.value)} aria-invalid={!!errors.name} />
              </Field>
              <Field id={FID.shortName} label="略称" style={{ flex: "1 1 180px" }} help={<Help testId="help-shortname" label="略称の説明" text={SHORT_NAME_HELP} />}>
                <input id={FID.shortName} className="inp" autoComplete="off" value={form.shortName} required aria-required="true" onKeyDown={guardComposingEnter} onChange={(e) => { shortTouched.current = true; set({ shortName: e.target.value }); }} aria-invalid={!!errors.shortName} />
              </Field>
            </div>
            <div className="row" style={{ marginTop: 12, alignItems: "flex-end" }}>
              <Field id={FID.nameKana} label="フリガナ（一覧の並び順）" style={{ flex: "2 1 300px" }}>
                <input id={FID.nameKana} className="inp" value={form.nameKana} onKeyDown={guardComposingEnter} onChange={(e) => set({ nameKana: e.target.value })} aria-invalid={!!errors.nameKana} />
              </Field>
              {/* 顧客コード・仕入先コード（#288、docs/101 §3）：役割があるときだけ。フリガナの行の空きに置く（画面の高さを変えない） */}
              {form.partnerType !== "vendor" ? (
                <Field id={FID.customerCode} label={PARTNER_PICKER_MSG.customerCode} style={{ flex: "1 1 180px" }}>
                  <input id={FID.customerCode} className="inp code" value={form.customerCode} maxLength={40} autoCapitalize="characters" onKeyDown={guardComposingEnter} onChange={(e) => set({ customerCode: e.target.value })} onBlur={() => set({ customerCode: partnerCodeOf(form.customerCode) })} aria-invalid={!!errors.customerCode} data-testid="partner-customer-code" />
                </Field>
              ) : null}
              {form.partnerType !== "client" ? (
                <Field id={FID.vendorCode} label={PARTNER_PICKER_MSG.vendorCode} style={{ flex: "1 1 180px" }}>
                  <input id={FID.vendorCode} className="inp code" value={form.vendorCode} maxLength={40} autoCapitalize="characters" onKeyDown={guardComposingEnter} onChange={(e) => set({ vendorCode: e.target.value })} onBlur={() => set({ vendorCode: partnerCodeOf(form.vendorCode) })} aria-invalid={!!errors.vendorCode} data-testid="partner-vendor-code" />
                </Field>
              ) : null}
            </div>
            <div className="row" style={{ marginTop: 12, alignItems: "flex-end" }}>
              <div className="field" style={{ flex: "0 0 auto" }}>
                <label>{L.customer}／{L.vendor}</label>
                <div className="seg" role="group" aria-label={`${L.customer}／${L.vendor}`} data-testid="partner-type">
                  {(["client", "vendor", "both"] as PartnerType[]).map((k) => (
                    <button key={k} type="button" aria-pressed={form.partnerType === k} onClick={() => set({ partnerType: k })}>
                      {k === "client" ? L.customer : k === "vendor" ? L.vendor : "両方"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field" style={{ flex: "0 0 auto" }}>
                <label>法人／個人</label>
                <div className="seg" role="group" aria-label="法人／個人">
                  {(["corporate", "individual"] as PartnerKind[]).map((k) => (
                    <button key={k} type="button" aria-pressed={form.kind === k} onClick={() => set({ kind: k, honorific: k === "individual" ? "様" : "御中" })}>
                      {k === "corporate" ? "法人" : "個人"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field" style={{ flex: "0 0 auto" }}>
                <label>敬称（帳票の宛名）</label>
                <div className="seg" role="group" aria-label="敬称">
                  {HONORIFICS.map((h) => (
                    <button key={h || "none"} type="button" aria-pressed={form.honorific === h} onClick={() => set({ honorific: h })}>
                      {HONORIFIC_LABEL[h]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ---- 住所と連絡先 ---- */}
          <section className="paper" style={sec}>
            <H2>住所と連絡先</H2>
            <AddressFields value={{ postalCode: form.postalCode, address: form.address, building: form.building }} onChange={(v) => set(v)} ids={{ zip: FID.postalCode!, address: FID.address!, building: FID.building! }} />
            <div className="tel-stack" style={{ marginTop: 12 }}>
              <PhoneFields id={FID.phone!} label="TEL" value={form.phone} onChange={(phone) => set({ phone })} invalid={!!errors.phone} />
              <PhoneFields id={FID.fax!} label="FAX" value={form.fax} onChange={(fax) => set({ fax })} invalid={!!errors.fax} />
            </div>
          </section>

          {/* ---- 請求 ---- */}
          <section className="paper" style={sec}>
            <H2>
              請求
              <Help testId="help-billing" label="請求の説明" text={`振込手数料が「先方」なら、請求書を作成するとき備考に「${TRANSFER_FEE_NOTE}」が入ります（請求書ごとに削除できます）。`} />
            </H2>
            <div className="field" style={{ marginBottom: 12 }}>
              <label>支払条件（請求書のお支払期限をここから計算）</label>
              <div className="row" style={{ gap: 12, flexWrap: "wrap" }} data-testid="terms-new">
                {sel("pn_tc", "締め日", form.terms.closingDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => set({ terms: { ...form.terms, closingDay: n as TermDay } }))}
                {sel("pn_tm", "支払", form.terms.paymentMonthOffset, MONTHS, (n) => TERM_MONTH_LABEL[n as TermMonth], (n) => set({ terms: { ...form.terms, paymentMonthOffset: n as TermMonth } }))}
                {sel("pn_td", "", form.terms.paymentDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => set({ terms: { ...form.terms, paymentDay: n as TermDay } }))}
              </div>
            </div>
            <div className="field">
              <label>振込手数料の負担</label>
              <div className="seg" role="group" aria-label="振込手数料の負担">
                {(["customer", "us"] as TransferFeeBearer[]).map((b) => (
                  <button key={b} type="button" aria-pressed={form.transferFeeBearer === b} onClick={() => set({ transferFeeBearer: b })}>
                    {TRANSFER_FEE_LABEL[b]}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* ---- 法人番号と登録番号 ---- */}
          <section className="paper" style={sec}>
            <H2>法人番号と登録番号（インボイス）</H2>
            <div className="row" style={{ alignItems: "flex-end" }}>
              <Field id={FID.corporateNumber} label="法人番号（13桁）" style={{ flex: "1 1 220px" }}>
                <input id={FID.corporateNumber} className="inp num" inputMode="numeric" value={form.corporateNumber} onKeyDown={guardComposingEnter} onChange={(e) => set({ corporateNumber: e.target.value })} aria-invalid={!!errors.corporateNumber} />
              </Field>
              <Field id={FID.invoiceRegistrationNo} label="登録番号（T＋13桁）" style={{ flex: "1 1 240px" }}>
                <input id={FID.invoiceRegistrationNo} className="inp num" value={form.invoiceRegistrationNo} placeholder={form.corporateNumber ? `T${form.corporateNumber}` : undefined} onKeyDown={guardComposingEnter} onChange={(e) => set({ invoiceRegistrationNo: e.target.value })} aria-invalid={!!errors.invoiceRegistrationNo} />
              </Field>
            </div>
            {canLookup ? <NtaSource style={{ marginTop: 8 }} /> : null}
          </section>

          {/* ---- 社内メモ ---- */}
          <section className="paper" style={sec}>
            <H2>社内メモ（お客様には出ません）</H2>
            <textarea id={FID.memo} className="inp" rows={3} value={form.memo} onChange={(e) => set({ memo: e.target.value })} aria-invalid={!!errors.memo} style={{ minHeight: 96, resize: "vertical" }} />
          </section>

          <div className="savebar" data-notice-above="">
            <ErrorText>{serverError ?? firstError}</ErrorText>
            <span className="sp" />
            <Link className="btn quiet" to="/partners">取消</Link>
            <button type="submit" className="btn primary" disabled={create.isPending} data-testid="partner-create">{who}登録</button>
          </div>
        </form>

        {/* ---- 右の列（詳細の画面と同じ） ---- */}
        <aside className="pdside">
          <section className="paper">
            <h2 className="hint" style={{ fontWeight: 700, margin: "0 0 10px" }}>帳票の宛名</h2>
            <div className="pdaddressee">
              {form.postalCode ? <span className="hint">〒{formatZip(form.postalCode)}</span> : null}
              {form.address || form.building ? <span className="hint">{[form.address, form.building].filter(Boolean).join(" ")}</span> : null}
              <span className="to">{form.name || "○○"}{form.honorific ? `　${form.honorific}` : ""}</span>
            </div>
          </section>

          {/* 担当者（1人目。2人目からは登録のあとで） */}
          <section className="paper" data-testid="new-contact">
            <h2>担当者<Help testId="help-contacts" label="担当者の説明" text={`${CONTACTS_HELP}2人目からは、登録のあとの画面で追加します。`} /></h2>
            <Field id={FID.contactName} label="氏名" style={{ marginBottom: 10 }}>
              <input id={FID.contactName} className="inp" autoComplete="off" value={form.contactName} onKeyDown={guardComposingEnter} onChange={(e) => set({ contactName: e.target.value })} aria-invalid={!!errors.contactName} />
            </Field>
            <div className="row" style={{ alignItems: "flex-end", marginBottom: 10 }}>
              <Field id="pn_contactDepartment" label="部署" style={{ flex: "1 1 120px" }}>
                <input id="pn_contactDepartment" className="inp" autoComplete="off" value={form.contactDepartment} onKeyDown={guardComposingEnter} onChange={(e) => set({ contactDepartment: e.target.value })} />
              </Field>
              <Field id="pn_contactTitle" label="肩書" style={{ flex: "1 1 100px" }}>
                <input id="pn_contactTitle" className="inp" autoComplete="off" value={form.contactTitle} onKeyDown={guardComposingEnter} onChange={(e) => set({ contactTitle: e.target.value })} />
              </Field>
            </div>
            <div style={{ marginBottom: 10 }}>
              <PhoneFields id={FID.mobile!} label="携帯" value={form.mobile} onChange={(mobile) => set({ mobile })} invalid={!!errors.mobile} />
            </div>
            <Field id={FID.email} label="メール">
              <input id={FID.email} className="inp" type="email" inputMode="email" autoComplete="off" value={form.email} onKeyDown={guardComposingEnter} onChange={(e) => set({ email: e.target.value })} aria-invalid={!!errors.email} />
            </Field>
          </section>

          {/* 支店・営業所（登録のあとで） */}
          <section className="paper">
            <h2 style={{ margin: 0 }}>支店・営業所（登録のあとで追加）</h2>
          </section>
        </aside>
      </div>

      {canLookup ? <CorporateLookupDialog open={lookup} initialQuery={form.corporateNumber || form.name} onApply={applyCorporate} onClose={() => setLookup(false)} /> : null}
      {node}
    </>
  );
}
